import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { twMerge } from 'tailwind-merge';
import CheckoutWizardRouter from './CheckoutWizardRouter';
import { CheckoutWizardRouterProps } from './CheckoutWizardInterfaces';
import WizardSteps from '../Step/WizardSteps';
import { Cart, Envelope, SelectedEnvelope } from '../Capsules/Envelopes';
import StepsAndDonee from '../Step/StepsAndDonee';
import GiveButton from './CartWizard/Payment/GiveButton';
import {
    deleteCartLocal,
    getCartLocal,
    saveCartLocal,
} from './CartLocalStorage';
import { usePushEvents } from '../../../../hooks/PushEvents';
import { useQueryString } from '../../../../hooks/useQueryString';
import { logError, logInfo, logging } from '../../../../utils/logError';
import {
    deleteDonationOnGiftSummary,
    errorOnGiftSummary,
    giftSuccessfullySubmitted,
} from '../../../../hooks/PushEvents/clevertapEvents';
import { requestRecaptchaV3Token } from '../../../Wrappers/ReCaptchaV3';
import { Wallet } from '../../../../providers/DonorProvider/interfaces';
import MetaTags from '../../../MetaTags';
import { HeartLoading } from '../../../HeartLoading';
import { useNavigate } from 'react-router-dom';
import { ROUTES } from '../../../../utils/routes';
import { LogLevel } from '../../../../utils/logger/models';
import {
    RetryDonation,
    EnvelopeWithAmount,
    retryDonationQueryString,
} from './RetryDonation';
import { DonationsQueryParams } from './utils';
import MainMobileBanner, { showInformationBanner } from './MainMobileBanner';
import { isMobile } from '../../../GetAppModal';
import { FeatureFlag } from '../../../../constants';
import { useFeatureFlag } from '../../../../providers';
import {
    ApiHandler,
    ErrorResponse,
    RecurringTypes,
    useDonee,
    useDonorProfile,
    useGiftSummary,
} from '../../../../api';
import { getTotalCartAmount } from '../../../../utils';

const getCarEnvelopes = (
    parsedDetails: EnvelopeWithAmount[],
    envelopes: Envelope[],
) => {
    const result: Cart['envelopes'] = {};
    parsedDetails.forEach((d) => {
        const envelope = envelopes.find((e) => e.envelopeId === d.envelopeId);
        if (envelope) {
            result[d.envelopeId] = { ...envelope, amount: d.amount };
        }
    });
    return result;
};

export const setDonationDetails = (
    doneeId: string,
    d: DonationsQueryParams[],
) => {
    return `/${doneeId}?details=${JSON.stringify(d)}`;
};

const maxEnvelopes = 10;
const duplicateDonationErrorCode = 1292;
const getDonationDetails = (
    details: string | null,
    envelopes: Envelope[] | undefined,
): Cart['envelopes'] | undefined => {
    if (!details || !envelopes) return undefined;
    try {
        const parsedDetails = JSON.parse(details) as DonationsQueryParams[];
        if (parsedDetails && typeof parsedDetails === 'object') {
            if (parsedDetails.length > maxEnvelopes) {
                return undefined;
            }
            return getCarEnvelopes(parsedDetails, envelopes);
        }
    } catch (e) {
        logError('Error parsing donation details', {
            error: e,
            details,
            envelopes,
        });
    }
    return undefined;
};

let firstRender: number = 0;
const firstRenderRoute = (): CheckoutWizardRouterProps['route'] => {
    if (firstRender < 2) {
        firstRender++;
        return 'cart';
    }
    return 'amount';
};

const CheckoutWizard: React.FC<{ className?: string }> = ({ className }) => {
    const push = useNavigate();
    const { donee } = useDonee();
    const { donorProfile, isLoading: isDonorProfileLoading } =
        useDonorProfile();
    const { incrementLocalTotalGiftsCount } = useGiftSummary();
    const { donor } = donorProfile || {};
    const isLoggedIn = Boolean(donorProfile);
    const { pushEvent, extendProfile } = usePushEvents();
    const query = useQueryString();
    const [retryDonationId, setRetryDonationId] = useState<string | undefined>(
        query.get(retryDonationQueryString) || undefined,
    );
    const details = getDonationDetails(query.get('details'), donee?.envelopes);
    const { isFeatureEnabled } = useFeatureFlag();
    const [showBanner, setShowBanner] = useState<boolean>(
        !isDonorProfileLoading &&
            !isLoggedIn &&
            showInformationBanner &&
            isMobile &&
            isFeatureEnabled(FeatureFlag.OGA_INFORMATION_BANNER),
    );
    const [requestLoading, setRequestLoading] = useState<boolean>(false);
    const [requestError, setRequestError] = useState<ErrorResponse | undefined>(
        undefined,
    );

    useEffect(() => {
        const show =
            !isDonorProfileLoading &&
            !isLoggedIn &&
            showInformationBanner &&
            isMobile &&
            isFeatureEnabled(FeatureFlag.OGA_INFORMATION_BANNER);
        setShowBanner(show);
    }, [isLoggedIn, isFeatureEnabled, setShowBanner, isDonorProfileLoading]);

    const handleClose = useCallback(() => {
        setShowBanner(false);
    }, [setShowBanner]);

    const { queryAmount, localCart, isDefaultEmptyCart } = useMemo(() => {
        const queryAmount = parseInt(query.get('amount') || '', 10) || 0;
        const { cart, isEmpty } = getCartLocal(donee?.idShort, queryAmount);
        return { queryAmount, localCart: cart, isDefaultEmptyCart: isEmpty };
    }, [query, donee?.idShort]);

    const [forceDuplicateDonation, setForceDuplicateDonation] =
        useState<boolean>(false);
    const [route, setRoute] = useState<CheckoutWizardRouterProps['route']>(
        isDefaultEmptyCart
            ? queryAmount
                ? 'envelopes'
                : 'amount'
            : firstRenderRoute(),
    );
    const [cart, setCart] = useState<Cart>(localCart);
    const [isGeneratingCaptcha, setIsGeneratingCaptcha] =
        useState<boolean>(false);

    const handleFailedDonation = useCallback(
        (envelopes: EnvelopeWithAmount[], callback: () => unknown) => {
            if (!donee?.envelopes) return;
            setCart((s) => ({
                ...s,
                envelopes: getCarEnvelopes(envelopes, donee.envelopes),
            }));
            setRoute('cart');
            callback();
        },
        [setCart, donee?.envelopes],
    );

    useEffect(() => {
        if (!details) return;
        const e = Object.keys(details).filter((e) => e !== 'new');
        if (e.length === 0) return;
        setCart((s) => ({
            ...s,
            envelopes: details,
        }));
        setRoute('cart');
    }, [details]);

    useEffect(() => {
        if (route === 'amount') {
            return;
        }
        window.onpopstate = () => {
            push(ROUTES.main);
        };
        return () => {
            window.onpopstate = null;
        };
        // listen only to route changes to avoid resetting on back button multiple times
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [route]);

    useEffect(() => {
        donee?.idShort && saveCartLocal(donee.idShort, cart);
        // listen only to cart changes to avoid saving multiple times on re-render
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [cart]);

    useEffect(() => {
        if (queryAmount) {
            setRoute('envelopes');
        } else if (getTotalCartAmount(cart.envelopes).amount === 0) {
            setRoute('amount');
        }
        // listen only to queryAmount changes to avoid resetting route multiple times on re-render
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [queryAmount]);

    const sendDonationRequest = useCallback(
        async (
            cart: Cart,
            doneeId: string,
            forceDuplicate: boolean,
            captchaToken: string | undefined,
        ) => {
            setRequestLoading(true);
            setRequestError(undefined);
            const request = await ApiHandler.instance.gift.sendDonationRequest(
                cart,
                doneeId,
                forceDuplicate,
                captchaToken,
            );

            if (request.success) {
                const payload = request.data.response.payload;
                const totalAmountFromApi = payload.amount;
                const cartInfo = getTotalCartAmount(cart.envelopes);
                const totalAmount = totalAmountFromApi || cartInfo.amount;
                const cartForEvent = {
                    cart: { ...cart, wallet: undefined },
                    totalAmountFromApi,
                    totalAmount,
                    ogaVersions: '4.0',
                };
                if (!totalAmountFromApi) {
                    logging(
                        'Unable to get total from API',
                        cartForEvent,
                        LogLevel.CRITICAL,
                    );
                }
                setForceDuplicateDonation(false);
                logInfo('Successful donation.', cartForEvent);
                pushEvent(giftSuccessfullySubmitted, {
                    totalAmount,
                    loggedIn: Boolean(donor),
                    doneeId: donee?.idShort,
                    amount: totalAmount,
                    envelopeCount: cartInfo.envelopes.length,
                    envelope:
                        cartInfo.envelopes.length === 1
                            ? cartInfo.envelopes[0].name
                            : 'Multiple',
                });
                setRoute('thank-you');
                deleteCartLocal();
                setCart({
                    envelopes: {},
                    recurring: 'None',
                    wallet: undefined,
                    receipt: {
                        totalAmount,
                        payload,
                        receiptId: payload.transactionId,
                    },
                });
                extendProfile(incrementLocalTotalGiftsCount());
            } else {
                if (request.error.status === duplicateDonationErrorCode) {
                    setForceDuplicateDonation(true);
                }
                const { amount, envelopes } = getTotalCartAmount(
                    cart.envelopes,
                );
                pushEvent(errorOnGiftSummary, {
                    amount,
                    totalAmount: amount,
                    loggedIn: Boolean(donor),
                    doneeId: donee?.idShort,
                    envelopeCount: envelopes.length,
                    errorMessage: request.error?.message,
                    envelope:
                        envelopes.length === 1 ? envelopes[0].name : 'Multiple',
                });
            }
            setRequestError(request.error);
            setRequestLoading(false);
        },
        [
            donee?.idShort,
            donor,
            extendProfile,
            pushEvent,
            incrementLocalTotalGiftsCount,
        ],
    );

    const sendDonation = useCallback(() => {
        if (!donee) {
            return;
        }
        setIsGeneratingCaptcha(true);
        requestRecaptchaV3Token(
            (captchaToken: string | undefined) => {
                window.setTimeout(() => {
                    setIsGeneratingCaptcha(false);
                }, 1000);
                sendDonationRequest(
                    cart,
                    donee.organization.doneeId,
                    forceDuplicateDonation,
                    captchaToken,
                );
            },
            undefined,
            'CheckoutWizard',
        );
    }, [cart, donee, forceDuplicateDonation, sendDonationRequest]);

    const addAmount = useCallback(
        (amount: SelectedEnvelope['amount']) => {
            setCart((s) => ({
                ...s,
                envelopes: { ...s.envelopes, new: amount },
            }));
        },
        [setCart],
    );

    const updateCartEnvelopes = useCallback(
        (e: SelectedEnvelope[]) => {
            setCart((s) => ({
                ...s,
                envelopes: e,
            }));
        },
        [setCart],
    );

    const setMessage = useCallback(
        (message: string) => {
            setCart((s) => ({ ...s, message }));
        },
        [setCart],
    );

    const setWallet = useCallback(
        (wallet: Wallet | undefined) => {
            setCart((s) => ({ ...s, wallet }));
        },
        [setCart],
    );

    const setRecurring = useCallback(
        (recurring: RecurringTypes) => {
            setCart((s) => ({ ...s, recurring }));
        },
        [setCart],
    );

    const addToCart = useCallback(
        (e: SelectedEnvelope) => {
            setCart((s) => {
                const envelope = s.envelopes[e.envelopeId];
                if (envelope) {
                    e.amount += envelope.amount;
                }
                return {
                    ...s,
                    envelopes: {
                        ...s.envelopes,
                        [e.envelopeId]: e,
                        new: undefined,
                    },
                };
            });
        },
        [setCart],
    );

    const removeFromCart = useCallback(
        (envelopeId: SelectedEnvelope['envelopeId']) => {
            setCart((s) => {
                const deletedEnvelope = s.envelopes[envelopeId];
                const newState = {
                    ...s,
                    envelopes: { ...s.envelopes, [envelopeId]: undefined },
                };
                const { envelopes, amount } = getTotalCartAmount(
                    newState.envelopes,
                );
                pushEvent(deleteDonationOnGiftSummary, {
                    loggedIn: Boolean(donor),
                    doneeId: donee?.idShort,
                    deletedAmount: deletedEnvelope?.amount,
                    deletedEnvelope: deletedEnvelope?.name,
                    amount: amount,
                    envelopeCount: envelopes.length,
                    paymentPresent: s.wallet ? 'Yes' : 'No',
                    envelope:
                        envelopes.length === 0
                            ? 'No Envelopes'
                            : envelopes.length === 1
                              ? envelopes[0].name
                              : 'Multiple',
                });
                return newState;
            });
        },
        [pushEvent, donor, donee?.idShort],
    );

    const isCart = route === 'cart';

    const metaTags = useMemo(() => {
        const organization = donee?.organization;
        return {
            title: organization?.official
                ? `${organization.name} with ${organization.official.name}`
                : organization?.name,
            description: organization?.doneeDescription || undefined,
            bannerUrl: organization?.banner || undefined,
            shareUrl: organization?.shareUrl || undefined,
        };
    }, [donee]);

    return (
        <div
            className={twMerge(
                'overflow-hidden rounded-[31px] transition-all duration-500 ease-[ease-in-out] shadow-high bg-white',
                className,
            )}
        >
            <RetryDonation
                retryDonationId={retryDonationId}
                setRetryDonationId={setRetryDonationId}
                handleFailedDonation={handleFailedDonation}
            />
            <MetaTags {...metaTags} />
            <WizardSteps
                cart={cart}
                route={route}
                setRoute={setRoute}
                addAmount={addAmount}
                addToCart={addToCart}
                setWallet={setWallet}
                removeFromCart={removeFromCart}
                setMessage={setMessage}
                setRecurring={setRecurring}
                updateCartEnvelopes={updateCartEnvelopes}
            />
            <StepsAndDonee
                cart={cart}
                route={route}
                hideSteps={route === 'thank-you' || showBanner}
                setRoute={setRoute}
                addAmount={addAmount}
                addToCart={addToCart}
                setWallet={setWallet}
                removeFromCart={removeFromCart}
                setMessage={setMessage}
                setRecurring={setRecurring}
                updateCartEnvelopes={updateCartEnvelopes}
            />
            <div
                className={`px-10 pt-10 ${
                    isCart ? 'pb-[44px]' : 'pb-20'
                } mobile:py-6 smallMobile:px-4 [&_.loading_svg]:max-h-[300px]`}
            >
                {requestLoading || isDonorProfileLoading ? (
                    <HeartLoading className={'loading'} />
                ) : showBanner ? (
                    <MainMobileBanner handleClose={handleClose} />
                ) : (
                    <CheckoutWizardRouter
                        cart={cart}
                        route={route}
                        setRoute={setRoute}
                        addAmount={addAmount}
                        addToCart={addToCart}
                        setWallet={setWallet}
                        removeFromCart={removeFromCart}
                        setMessage={setMessage}
                        setRecurring={setRecurring}
                        updateCartEnvelopes={updateCartEnvelopes}
                    />
                )}
            </div>
            {!showBanner && isCart && !requestLoading && (
                <GiveButton
                    cart={cart}
                    setCart={setCart}
                    setWallet={setWallet}
                    onSubmit={sendDonation}
                    disabled={isGeneratingCaptcha}
                    errorState={requestError}
                />
            )}
        </div>
    );
};

export default CheckoutWizard;
